import { useContext, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AppError, Loader, toast } from "@bfc-software/pallet";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { validatePageSearch } from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";

import {
  ACTIVATION_FILTERS,
  ActivationFilter,
  DEFAULT_ACTIVATION_FILTER,
  useTaskFilters,
  validateActivationFilter,
} from "../../-data/filters";
import { hasSearch } from "../../-data/task-model";
import { useSetPlanActivation, useTaskPlans } from "../../-data/use-plans";
import { useTaskDrawerSelection } from "../../-data/use-task-drawer-selection";
import { PlanCard } from "../../-components/plan-card";
import { TaskDetailDrawer } from "../../-components/task-detail-drawer";
import { ScopeChips, type ScopeChip } from "../../-components/scope-chips";
import styles from "../../tasks.module.css";

import { plansPageStateFrom } from "./-state";
import { PlanTasks } from "./-plan-tasks";

/**
 * Task plans: the release decision.
 *
 * Its own route, and therefore its own read (`use-plans`), its own model
 * (`plans-model`) and its own state union (`-state.ts` next door). The All tasks
 * tab shares none of them - only the filter bar the layout above draws, which
 * arrives through `useTaskFilters` and lives in the URL.
 *
 * The status chips are a URL filter like the search boxes, and for the same
 * reason: a manager looking at the plans still awaiting release is looking at
 * something they may want to send to someone else, and a chip held in `useState`
 * is a view that survives neither a reload nor a paste. It also means the chip
 * is a request rather than a predicate - `activation_state` on the listing (see
 * `activationQuery` in `use-plans.ts`), so the cards on screen are what the
 * service returned rather than what this file kept.
 *
 * Which cards are open stays component state, because it is not a filter: it
 * decides what else gets *fetched* - an open card reads its own plan's tasks,
 * which is why the card, not this file, owns that query.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/tasks/_layout/plans/",
)({
  /**
   * The page cursor, owned by the leaf.
   *
   * The filters and the chip belong to the layout above
   * (`validateTaskFiltersSearch`), because both tabs read them; a cursor names a
   * boundary in *this* listing's result set, so it means nothing on the All
   * tasks tab and is not carried there - the two listings hand out tokens of
   * their own and neither can read the other's.
   *
   * That split is also what returns a narrowed view to page 1: the layout's
   * `setSearch` rebuilds the params from its own schema, and this pair is simply
   * not in it.
   */
  validateSearch: validatePageSearch,
  component: RouteComponent,
});

function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  /**
   * The org the requests are made against.
   *
   * The URL carries the organisation's stub; the task planner's path segment is
   * the Stytch org id. `undefined` while a session is being minted, which the
   * reads below wait on rather than requesting `/undefined/task-plans`.
   */
  const orgId = AuthUtils.getOrgIdByStub(authState, org);

  const { filters, search, setSearch } = useTaskFilters();
  const page = Route.useSearch();
  const navigate = Route.useNavigate();
  /**
   * Which plan cards are open, by id.
   *
   * A set rather than one id: a manager comparing two waves opens both, and an
   * accordion that closes the first is a worse answer than a long page.
   */
  const [expanded, setExpanded] = useState<ReadonlyArray<string>>([]);

  /**
   * The chip in force, resolved once.
   *
   * An absent param is the default rather than "no filter" - the tab opens on
   * the plans awaiting release - so this is both what the request narrows by and
   * which chip is drawn as selected. Resolved here because the URL is this
   * route's to read; `plansQuery` takes it already decided.
   */
  const activationFilter = search.activation ?? DEFAULT_ACTIVATION_FILTER;

  const query = useTaskPlans({
    orgId,
    warehouseId: location,
    filters,
    activation: activationFilter,
    page,
  });
  const state = plansPageStateFrom(query);
  const activation = useSetPlanActivation(orgId);

  const onSetActivation = (planId: string, isActivated: boolean) => {
    if (activation.isPending) {
      logger.info("Avoiding concurrent plan activation change");
      return;
    }

    activation
      .setActivation(planId, isActivated)
      .then(() =>
        toast.success(
          t(isActivated ? "tasks.toast.activated" : "tasks.toast.deactivated", {
            plan: planId,
          }),
        ),
      )
      .catch((error: unknown) => {
        logger.warn("Plan activation change failed", { planId, error });
        // The likely case is a plan a selector started since this page loaded,
        // which the disabled button could not have known about. The refetch that
        // follows is what corrects the card.
        toast.error(
          t(
            isActivated
              ? "tasks.toast.activateFailed"
              : "tasks.toast.deactivateFailed",
            { plan: planId },
          ),
        );
      });
  };

  /**
   * The open task detail drawer.
   *
   * Above the state switch and rendered at the end of every branch that has
   * cards, so closing it is a URL change and not an unmount of the page under
   * it - see `useTaskDrawerSelection` for why the identity is in the URL and
   * the row is not.
   */
  const drawer = useTaskDrawerSelection();

  /*
    The chips carry no counts.

    Each chip is a different request, and the one that was made says nothing
    about the two that were not - a count taken from the page in hand would be
    the number of plans matching the *current* filter, sitting under a chip
    promising a different one.
  */
  const chips: ReadonlyArray<ScopeChip<ActivationFilter>> =
    ACTIVATION_FILTERS.map((key) => ({
      key,
      label: t(`tasks.activationFilter.${key}`),
    }));

  /*
    Above the state switch, so it is mounted whatever the read did. The filter is
    part of the request, which makes an empty or failed result something the
    filter itself may have caused - and a control that unmounts on the result it
    produced is one a manager cannot undo without editing the URL.
  */
  const scopeRow = (
    <div className={styles.scopeRow}>
      <ScopeChips
        chips={chips}
        selected={activationFilter}
        /*
          The default chip validates to `undefined`, and an undefined param is
          what TanStack drops from the URL - so the view the tab opens on has no
          param and every other chip carries one, the All chip included.

          `Awaiting Activation` also drops the selector, in this same navigation
          rather than in a second one. A plan that has released nothing holds an
          assignment to nobody, so the pair is a contradiction the layout's
          `validateTaskFiltersSearch` resolves by moving the chip to `All plans`
          (see `resolveActivation`) - the right answer to a pasted URL and the
          wrong one to a click, where the chip pressed is what the manager
          actually asked for.
        */
        onSelect={(key) =>
          setSearch({
            activation: validateActivationFilter(key),
            ...(key === ActivationFilter.NOT_ACTIVATED
              ? { selector: undefined }
              : {}),
          })
        }
        ariaLabel={t("tasks.activationFilter.ariaLabel")}
        // A search has already widened the request past the selected chip (see
        // `plansQuery`), so the chip is not what is on screen and the row has to
        // say so. Read off the filters rather than off the read: it is true from
        // the keystroke, not from whenever the request comes back.
        overrideNote={
          hasSearch(filters) ? t("tasks.search.overridePlans") : undefined
        }
      />
    </div>
  );

  switch (state.kind) {
    case "loading":
      return (
        <>
          {scopeRow}
          <div className={styles.loading} data-test-role="plans-loading">
            <Loader type="page" />
          </div>
        </>
      );
    case "failed":
      return (
        <>
          {scopeRow}
          <div className={styles.error} data-test-role="plans-failed">
            <AppError
              description={t("tasks.plans.failed")}
              resolution={t("tasks.failureSupport")}
            />
          </div>
        </>
      );
    case "loaded":
      break;
  }

  const { model, pagination } = state.value;

  return (
    <>
      {scopeRow}

      {/*
        Below the chips and directly above the cards, which is where the All
        tasks tab puts its pager too - so walking pages is the same gesture
        whichever tab is open.

        The plan listing reports `has_more` and a forward cursor and nothing
        else, so `ListPager` renders its forward-only fallback here rather than
        the numbered pager the other tab gets. Each tab takes whichever its own
        endpoint can answer for, and neither route changes when one of them
        starts counting.
      */}
      <div className={styles.pagerRow} data-test-role="pager-row">
        <ListPager
          pagination={pagination}
          isFetching={query.isFetching}
          // The cursor pair only; the filters and the chip are the layout's and
          // are left exactly as they stand.
          onChange={(next) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                cursor: next.cursor,
                dir: next.dir,
              }),
            })
          }
          t={t}
        />
      </div>

      {model.planRows.length > 0 ? (
        <div className={styles.plans}>
          {model.planRows.map((row) => (
            <PlanCard
              key={row.plan.id}
              row={row}
              isExpanded={expanded.includes(row.plan.id)}
              onToggle={() =>
                setExpanded((previous) =>
                  previous.includes(row.plan.id)
                    ? previous.filter((id) => id !== row.plan.id)
                    : [...previous, row.plan.id],
                )
              }
              onActivate={() => onSetActivation(row.plan.id, true)}
              onDeactivate={() => onSetActivation(row.plan.id, false)}
              isPending={activation.pendingPlanId === row.plan.id}
              tasks={
                <PlanTasks
                  orgId={orgId}
                  warehouseId={location}
                  plan={row.plan}
                  filters={filters}
                  onSelectTask={drawer.select}
                  t={t}
                />
              }
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className={styles.empty} data-test-role="plans-empty">
          {t("tasks.plans.empty")}
        </div>
      )}

      {drawer.selected ? (
        <TaskDetailDrawer
          orgId={orgId}
          planId={drawer.selected.identity.planId}
          taskId={drawer.selected.identity.taskId}
          task={drawer.selected.task}
          params={{ lang, org, location }}
          onClose={drawer.close}
          t={t}
        />
      ) : (
        <></>
      )}
    </>
  );
}
